import {Action, PayloadAction, createSlice} from '@reduxjs/toolkit'
import { DEFAULT_PAGE_SIZE } from '@/__core/constants/pagination';
import {Transaction, CreateTransactionRequest} from '../models/Transaction'
import { createAsyncThunk } from '@reduxjs/toolkit'
import { TransactionFilters } from './TransactionCRUD';
import { getTransactions } from './TransactionCRUD';

export interface ActionWithPayload<T> extends Action {
  payload?: T
}

export const actionTypes = {
  TransactionsRequested: '[Request Transactions] Action',
  TransactionsLoaded: '[Load Transactions] Transactions API',
  TransactionsFailed: '[Load Transactions] Transactions API Failed',
  TransactionRequested: '[Request Transaction] Action',
  TransactionLoaded: '[Load Transaction] Transaction API',
  TransactionFailed: '[Load Transaction] Transaction API Failed',
  TransactionCreated: '[Create Transaction] Action',
  TransactionUpdated: '[Update Transaction] Action',
  TransactionDeleted: '[Delete Transaction] Action',
}

// Create transaction thunk
export const createTransactionThunk = createAsyncThunk(
  'transactions/createTransaction',
  async (transactionData: CreateTransactionRequest, { rejectWithValue }) => {
    try {
      const { default: axiosInstance } = await import('@/setup/axios/axiosInstance');
      
      // If transaction is for a specific order, use order endpoint
      if (transactionData.order) {
        const response = await axiosInstance.post(`/orders/${transactionData.order}/transactions/`, transactionData);
        return response.data;
      } else {
        // Otherwise use general transactions endpoint
        const response = await axiosInstance.post('/finance/transactions/', transactionData);
        return response.data;
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create transaction');
    }
  }
);

// Update transaction thunk
export const updateTransactionThunk = createAsyncThunk(
  'transactions/updateTransaction',
  async (transactionData: { id: string } & Partial<CreateTransactionRequest>, { rejectWithValue }) => {
    try {
      const { id, ...updateData } = transactionData;
      
      // Remove fields that shouldn't be sent in update request
      const { created_by, ...cleanUpdateData } = updateData;
      
      // Convert empty strings to null for optional fields
      const cleanedData = {
        ...cleanUpdateData,
        customer: cleanUpdateData.customer || null,
        employee: cleanUpdateData.employee || null,
        debt_type: cleanUpdateData.debt_type || null,
        supplier: cleanUpdateData.supplier || null,
      };
      
      
      const { default: axiosInstance } = await import('@/setup/axios/axiosInstance');
      const response = await axiosInstance.patch(`/finance/transactions/${id}/`, cleanedData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update transaction');
    }
  }
);

// Delete transaction thunk
export const deleteTransactionThunk = createAsyncThunk(
  'transactions/deleteTransaction',
  async (transactionId: string, { rejectWithValue }) => {
    try {
      const { default: axiosInstance } = await import('@/setup/axios/axiosInstance');
      const response = await axiosInstance.delete(`/finance/transactions/${transactionId}/`);
      return { id: transactionId, ...response.data };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete transaction');
    }
  }
);

// Fetch transactions with filters thunk
export const fetchTransactionsWithFiltersThunk = createAsyncThunk(
  'transactions/fetchTransactionsWithFilters',
  async (filters: TransactionFilters) => {
    const response = await getTransactions(filters);
    return response.data;
  }
);

const readNumberValue = (source: Record<string, unknown>, keys: string[]): number | undefined => {
  for (const key of keys) {
    const parsedValue = Number(source[key]);
    if (!Number.isNaN(parsedValue)) {
      return Math.round(parsedValue);
    }
  }

  return undefined;
};

const normalizeSummaryPayload = (payload: any) => {
  const source = (payload?.data || payload?.summary || payload || {}) as Record<string, unknown>;

  const totalIncome = readNumberValue(source, ['total_receipt', 'total_income', 'totalIncome', 'income']) || 0;
  const totalExpense = readNumberValue(source, ['total_payment', 'total_expense', 'totalExpense', 'expense']) || 0;
  const supplierDebt = readNumberValue(source, ['supplier_debt', 'supplierDebt', 'debt']) || 0;
  const fundBalance = readNumberValue(source, ['cash_balance', 'fund_balance', 'fundBalance', 'balance']) ?? (totalIncome - totalExpense);
  const netFundAfterDebt = readNumberValue(source, ['net_fund', 'net_fund_after_debt', 'netFundAfterDebt']) ?? (fundBalance - supplierDebt);

  return {
    totalIncome,
    totalExpense,
    supplierDebt,
    fundBalance,
    netFundAfterDebt,
  };
};

const formatMonthLabel = (rawMonth: string): string => {
  const normalized = (rawMonth || '').trim();
  if (!normalized) {
    return '-';
  }

  const dashMonthYear = normalized.match(/^(\d{1,2})-(\d{4})$/);
  if (dashMonthYear) {
    const month = dashMonthYear[1].padStart(2, '0');
    return `${month}/${dashMonthYear[2]}`;
  }

  const yearDashMonth = normalized.match(/^(\d{4})-(\d{1,2})$/);
  if (yearDashMonth) {
    const month = yearDashMonth[2].padStart(2, '0');
    return `${month}/${yearDashMonth[1]}`;
  }

  return normalized;
};

const resolveMonthlyRowsFromPayload = (payload: any): Record<string, unknown>[] => {
  const directData = payload?.data;
  if (Array.isArray(directData)) {
    return directData as Record<string, unknown>[];
  }

  const candidates = [
    payload?.data?.monthly_summary,
    payload?.data?.items,
    payload?.results,
    payload?.monthly_summary,
    payload?.items,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate as Record<string, unknown>[];
    }
  }

  return [];
};

const normalizeSummaryByMonthPayload = (payload: any) => {
  const rows = resolveMonthlyRowsFromPayload(payload);

  return rows.map((row, index) => {
    const monthRaw = String(row.month || row.period || row.month_label || row.label || '').trim();
    const income = readNumberValue(row, ['total_receipt', 'total_income', 'income', 'receipt']) || 0;
    const expense = readNumberValue(row, ['total_payment', 'total_expense', 'expense', 'payment']) || 0;

    return {
      id: String(row.id || `${monthRaw || 'row'}-${index}`),
      month: formatMonthLabel(monthRaw),
      income,
      expense,
      cashBalance: readNumberValue(row, ['cash_balance', 'fund_balance', 'balance']) ?? (income + expense),
    };
  });
};

export const fetchTransactionSummaryThunk = createAsyncThunk(
  'transactions/fetchSummary',
  async (params: { month: string; company: string }, { rejectWithValue }) => {
    try {
      const { default: axiosInstance } = await import('@/setup/axios/axiosInstance');
      const response = await axiosInstance.get('/finance/transactions/report/', {
        params: {
          type: 'summary',
          month: params.month,
          company: params.company,
        },
      });

      return normalizeSummaryPayload(response.data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch transaction summary');
    }
  }
);

const normalizeByTypePayload = (payload: any) => {
  const rows = Array.isArray(payload?.data) ? payload.data : [];

  const typeMap = new Map<string, { id: string; code: string; name: string; category: 'receipt' | 'payment' }>();

  rows.forEach((monthRow: any) => {
    // New format: receipt.items[] / payment.items[]
    const receiptItems = Array.isArray(monthRow.receipt?.items) ? monthRow.receipt.items : [];
    const paymentItems = Array.isArray(monthRow.payment?.items) ? monthRow.payment.items : [];
    receiptItems.forEach((t: any) => {
      const id = String(t.transaction_type_id || t.transaction_type_code || '');
      if (id && !typeMap.has(id)) {
        typeMap.set(id, { id, code: String(t.transaction_type_code || id), name: String(t.transaction_type_name || ''), category: 'receipt' });
      }
    });
    paymentItems.forEach((t: any) => {
      const id = String(t.transaction_type_id || t.transaction_type_code || '');
      if (id && !typeMap.has(id)) {
        typeMap.set(id, { id, code: String(t.transaction_type_code || id), name: String(t.transaction_type_name || ''), category: 'payment' });
      }
    });
    // Fallback: old format breakdown_by_type[]
    const breakdowns = Array.isArray(monthRow.breakdown_by_type) ? monthRow.breakdown_by_type : [];
    breakdowns.forEach((breakdown: any) => {
      const category: 'receipt' | 'payment' = breakdown.type === 'receipt' ? 'receipt' : 'payment';
      const types = Array.isArray(breakdown.transaction_types) ? breakdown.transaction_types : [];
      types.forEach((t: any) => {
        const id = String(t.transaction_type_id || t.transaction_type_code || '');
        if (id && !typeMap.has(id)) {
          typeMap.set(id, { id, code: String(t.transaction_type_code || id), name: String(t.transaction_type_name || ''), category });
        }
      });
    });
  });

  // receipt types first, then payment types
  const types = Array.from(typeMap.values()).sort((a, b) => {
    if (a.category === b.category) return 0;
    return a.category === 'receipt' ? -1 : 1;
  });

  const monthlyRows = rows.map((monthRow: any) => {
    const label = String(monthRow.label || monthRow.month || '');
    const values: Record<string, number> = {};

    // New format
    const receiptItems = Array.isArray(monthRow.receipt?.items) ? monthRow.receipt.items : [];
    const paymentItems = Array.isArray(monthRow.payment?.items) ? monthRow.payment.items : [];
    [...receiptItems, ...paymentItems].forEach((t: any) => {
      const id = String(t.transaction_type_id || t.transaction_type_code || '');
      values[id] = Math.round(Number(t.actual_amount || t.total_amount || 0));
    });
    // Fallback: old format
    const breakdowns = Array.isArray(monthRow.breakdown_by_type) ? monthRow.breakdown_by_type : [];
    breakdowns.forEach((breakdown: any) => {
      const typeItems = Array.isArray(breakdown.transaction_types) ? breakdown.transaction_types : [];
      typeItems.forEach((t: any) => {
        const id = String(t.transaction_type_id || t.transaction_type_code || '');
        values[id] = Math.round(Number(t.actual_amount || t.total_amount || 0));
      });
    });

    return {
      month: label,
      values,
      totalReceipt: Math.round(Number(monthRow.total_receipt || 0)),
      totalPayment: Math.round(Number(monthRow.total_payment || 0)),
      profit: Math.round(Number(monthRow.profit || 0)),
      profitPercentage: Number(monthRow.profit_percentage || 0),
    };
  });

  return { types, monthlyRows };
};

const normalizeRatioPayload = (payload: any) => {
  const rows = Array.isArray(payload?.data) ? payload.data : [];
  const typeMap = new Map<string, { id: string; name: string; code: string }>();

  rows.forEach((monthRow: any) => {
    const items = Array.isArray(monthRow.items) ? monthRow.items : [];
    items.forEach((item: any) => {
      const id = String(item.id || item.transaction_type_id || item.transaction_type_code || '');
      if (!id || typeMap.has(id)) {
        return;
      }

      typeMap.set(id, {
        id,
        name: String(item.name || id),
        code: String(item.transaction_type_code || item.code || id),
      });
    });
  });

  const types = Array.from(typeMap.values());

  const monthlyRows = rows.map((monthRow: any) => {
    const label = String(monthRow.label || monthRow.month || '');
    const values: Record<string, number | null> = {};
    const items = Array.isArray(monthRow.items) ? monthRow.items : [];

    items.forEach((item: any) => {
      const id = String(item.id || '');
      if (!id) {
        return;
      }

      const rawRatio = item.ratio_payment_to_receipt;
      values[id] = rawRatio === null || rawRatio === undefined ? null : Number(rawRatio);
    });

    return {
      month: label,
      values,
      totalCostRevenueRatio: monthRow.total_cost_revenue_ratio === null || monthRow.total_cost_revenue_ratio === undefined ? null : Number(monthRow.total_cost_revenue_ratio),
      profitPercentage: monthRow.profit_percentage === null || monthRow.profit_percentage === undefined ? null : Number(monthRow.profit_percentage),
      totalRatio: monthRow.total_ratio === null || monthRow.total_ratio === undefined ? null : Number(monthRow.total_ratio),
    };
  });

  return { types, monthlyRows };
};

export const fetchTransactionSummaryByTypeThunk = createAsyncThunk(
  'transactions/fetchSummaryByType',
  async (params: { month: string; company: string }, { rejectWithValue }) => {
    try {
      const { default: axiosInstance } = await import('@/setup/axios/axiosInstance');
      const response = await axiosInstance.get('/finance/transactions/report/', {
        params: {
          type: 'summary_by_transaction_type',
          month: params.month,
          company: params.company,
        },
      });

      return normalizeByTypePayload(response.data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch by-type transaction summary');
    }
  }
);

export const fetchTransactionRatioPaymentRevenueThunk = createAsyncThunk(
  'transactions/fetchRatioPaymentRevenue',
  async (params: { month: string; company: string }, { rejectWithValue }) => {
    try {
      const { default: axiosInstance } = await import('@/setup/axios/axiosInstance');
      const response = await axiosInstance.get('/finance/transactions/report/', {
        params: {
          type: 'ratio_payment_and_revenue',
          month: params.month,
          company: params.company,
        },
      });

      return normalizeRatioPayload(response.data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch payment/revenue ratio summary');
    }
  }
);

export const fetchTransactionSummarySupplierThunk = createAsyncThunk(
  'transactions/fetchSummarySupplier',
  async (params: { month: string; company: string }, { rejectWithValue }) => {
    try {
      const { default: axiosInstance } = await import('@/setup/axios/axiosInstance');
      const response = await axiosInstance.get('/finance/transactions/report/', {
        params: {
          type: 'summary_supplier',
          month: params.month,
          company: params.company,
        },
      });

      const rows = Array.isArray(response.data?.data) ? response.data.data : [];
      return rows.map((row: any) => ({
        supplierId: String(row.supplier_id || ''),
        supplierCode: String(row.supplier_code || ''),
        supplierName: String(row.supplier_name || ''),
        totalAmount: Math.round(Number(row.supplier_total_amount || 0)),
        paidAmount: Math.round(Number(row.supplier_paid_amount || 0)),
        debtAmount: Math.round(Number(row.supplier_debt || 0)),
      }));
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch supplier summary');
    }
  }
);

export const fetchTransactionSummaryByMonthThunk = createAsyncThunk(
  'transactions/fetchSummaryByMonth',
  async (params: { month: string; company: string }, { rejectWithValue }) => {
    try {
      const { default: axiosInstance } = await import('@/setup/axios/axiosInstance');
      const response = await axiosInstance.get('/finance/transactions/report/', {
        params: {
          type: 'summary_by_month',
          month: params.month,
          company: params.company,
        },
      });

      return normalizeSummaryByMonthPayload(response.data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch monthly transaction summary');
    }
  }
);

// Search transactions thunk (with debouncing support)
export const searchTransactionsThunk = createAsyncThunk(
  'transactions/searchTransactions',
  async (searchQuery: string, { getState, dispatch }) => {
    const state = getState() as any;
    const currentFilters = state.transactions.filters;
    
    // Update search in filters and fetch
    const updatedFilters = { ...currentFilters, search: searchQuery, page: 1 };
    dispatch(setFilters(updatedFilters));
    
    const response = await getTransactions(updatedFilters);
    return response.data;
  }
);

const initialFilters: TransactionFilters = {
  company: 'all',
  dateFrom: '',
  dateTo: '',
  documentDateFrom: '',
  documentDateTo: '',
  moneyFlow: '',
  transactionTypeCategory: '',
  account: '',
  orderCode: '',
  amountFrom: '',
  amountTo: '',
  totalValueFrom: '',
  totalValueTo: '',
  notes: '',
  employee: '',
  customer: '',
  supplier: '',
  creationDateFrom: '',
  creationDateTo: '',
  updaterName: '',
  orderStatus: '',
  search: '',
  page: 1,
  page_size: DEFAULT_PAGE_SIZE,
};

interface TransactionPagination {
  count: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

const initialPagination: TransactionPagination = {
  count: 0,
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  totalPages: 1,
  hasNext: false,
  hasPrevious: false,
};

const getTransactionsFromPayload = (payload: any): Transaction[] => payload?.results || payload || [];

const getPaginationFromPayload = (payload: any, fallbackTransactions: Transaction[]): TransactionPagination => {
  if (!payload || Array.isArray(payload)) {
    const count = fallbackTransactions.length;
    return {
      count,
      page: 1,
      pageSize: count || initialPagination.pageSize,
      totalPages: count > 0 ? 1 : initialPagination.totalPages,
      hasNext: false,
      hasPrevious: false,
    };
  }

  const fallbackPageSize = fallbackTransactions.length || initialPagination.pageSize;
  const pageSize = payload.page_size ?? payload.pageSize ?? fallbackPageSize;
  const count = payload.count ?? fallbackTransactions.length;

  return {
    count,
    page: payload.page ?? 1,
    pageSize,
    totalPages: payload.total_pages ?? payload.totalPages ?? Math.max(1, Math.ceil(count / Math.max(pageSize, 1))),
    hasNext: payload.has_next ?? payload.hasNext ?? false,
    hasPrevious: payload.has_previous ?? payload.hasPrevious ?? false,
  };
};

const initialTransactionState: ITransactionState = {
  transactions: [],
  transaction: undefined,
  loading: false,
  summaryLoading: false,
  summaryReport: {
    totalIncome: 0,
    totalExpense: 0,
    fundBalance: 0,
    supplierDebt: 0,
    netFundAfterDebt: 0,
  },
  summaryError: undefined,
  monthlySummaryReport: [],
  monthlySummaryLoading: false,
  monthlySummaryError: undefined,
  supplierSummaryReport: [],
  supplierSummaryLoading: false,
  supplierSummaryError: undefined,
  byTypeSummaryReport: { types: [], monthlyRows: [] },
  byTypeSummaryLoading: false,
  byTypeSummaryError: undefined,
  ratioSummaryReport: { types: [], monthlyRows: [] },
  ratioSummaryLoading: false,
  ratioSummaryError: undefined,
  error: undefined,
  filters: initialFilters,
  appliedFilters: initialFilters,
  pagination: initialPagination,
}

export interface ITransactionState {
  transactions: Transaction[]
  transaction?: Transaction
  loading: boolean
  summaryLoading: boolean
  summaryReport: {
    totalIncome: number
    totalExpense: number
    fundBalance: number
    supplierDebt: number
    netFundAfterDebt: number
  }
  summaryError?: string
  monthlySummaryReport: Array<{
    id: string
    month: string
    income: number
    expense: number
    cashBalance: number
  }>
  monthlySummaryLoading: boolean
  monthlySummaryError?: string
  supplierSummaryReport: Array<{
    supplierId: string
    supplierCode: string
    supplierName: string
    totalAmount: number
    paidAmount: number
    debtAmount: number
  }>
  supplierSummaryLoading: boolean
  supplierSummaryError?: string
  byTypeSummaryReport: {
    types: Array<{ id: string; code: string; name: string; category: 'receipt' | 'payment' }>
    monthlyRows: Array<{ month: string; values: Record<string, number>; totalReceipt: number; totalPayment: number; profit: number; profitPercentage: number }>
  }
  byTypeSummaryLoading: boolean
  byTypeSummaryError?: string
  ratioSummaryReport: {
    types: Array<{ id: string; name: string }>
    monthlyRows: Array<{ month: string; values: Record<string, number | null>; totalCostRevenueRatio: number | null; profitPercentage: number | null; totalRatio: number | null }>
  }
  ratioSummaryLoading: boolean
  ratioSummaryError?: string
  error?: string
  filters: TransactionFilters
  appliedFilters: TransactionFilters
  pagination: TransactionPagination
}

// Slice for modern Redux Toolkit
const transactionSlice = createSlice({
  name: 'transactions',
  initialState: initialTransactionState,
  reducers: {
    // Filter actions
    setFilter: (state, action: PayloadAction<{ field: keyof TransactionFilters; value: string | number | boolean }>) => {
      (state.filters as Record<string, string | number | boolean | undefined>)[action.payload.field] = action.payload.value;
    },
    setFilters: (state, action: PayloadAction<Partial<TransactionFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = initialFilters;
    },
    applyFilters: (state) => {
      state.appliedFilters = { ...state.filters };
    },
    clearAppliedFilters: (state) => {
      state.appliedFilters = initialFilters;
    },
    // Error handling
    clearError: (state) => {
      state.error = undefined;
    },
    clearSummaryError: (state) => {
      state.summaryError = undefined;
    },
    clearMonthlySummaryError: (state) => {
      state.monthlySummaryError = undefined;
    },
    clearSupplierSummaryError: (state) => {
      state.supplierSummaryError = undefined;
    },
    clearByTypeSummaryError: (state) => {
      state.byTypeSummaryError = undefined;
    },
    clearRatioSummaryError: (state) => {
      state.ratioSummaryError = undefined;
    },
    // Update transaction in state (for temporary order_name injection)
    updateTransaction: (state, action: PayloadAction<Transaction>) => {
      const index = state.transactions.findIndex(t => t.id === action.payload.id);
      if (index !== -1) {
        state.transactions[index] = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch transactions with filters
    builder
      .addCase(fetchTransactionsWithFiltersThunk.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(fetchTransactionsWithFiltersThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.filters = { ...state.filters, ...action.meta.arg };
        state.appliedFilters = { ...state.appliedFilters, ...action.meta.arg };
        const transactions = getTransactionsFromPayload(action.payload);
        state.transactions = transactions;
        state.pagination = getPaginationFromPayload(action.payload, transactions);
      })
      .addCase(fetchTransactionsWithFiltersThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch transactions with filters';
      });

    // Search transactions
    builder
      .addCase(searchTransactionsThunk.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(searchTransactionsThunk.fulfilled, (state, action) => {
        state.loading = false;
        const transactions = getTransactionsFromPayload(action.payload);
        state.transactions = transactions;
        state.pagination = getPaginationFromPayload(action.payload, transactions);
        state.error = undefined;
      })
      .addCase(searchTransactionsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to search transactions';
      });

    // Fetch summary for finance report
    builder
      .addCase(fetchTransactionSummaryThunk.pending, (state) => {
        state.summaryLoading = true;
        state.summaryError = undefined;
      })
      .addCase(fetchTransactionSummaryThunk.fulfilled, (state, action) => {
        state.summaryLoading = false;
        state.summaryReport = action.payload;
        state.summaryError = undefined;
      })
      .addCase(fetchTransactionSummaryThunk.rejected, (state, action) => {
        state.summaryLoading = false;
        state.summaryError = action.payload as string || 'Failed to fetch transaction summary';
      });

    builder
      .addCase(fetchTransactionSummaryByMonthThunk.pending, (state) => {
        state.monthlySummaryLoading = true;
        state.monthlySummaryError = undefined;
      })
      .addCase(fetchTransactionSummaryByMonthThunk.fulfilled, (state, action) => {
        state.monthlySummaryLoading = false;
        state.monthlySummaryReport = action.payload;
        state.monthlySummaryError = undefined;
      })
      .addCase(fetchTransactionSummaryByMonthThunk.rejected, (state, action) => {
        state.monthlySummaryLoading = false;
        state.monthlySummaryError = action.payload as string || 'Failed to fetch monthly transaction summary';
      });

    builder
      .addCase(fetchTransactionSummarySupplierThunk.pending, (state) => {
        state.supplierSummaryLoading = true;
        state.supplierSummaryError = undefined;
      })
      .addCase(fetchTransactionSummarySupplierThunk.fulfilled, (state, action) => {
        state.supplierSummaryLoading = false;
        state.supplierSummaryReport = action.payload;
        state.supplierSummaryError = undefined;
      })
      .addCase(fetchTransactionSummarySupplierThunk.rejected, (state, action) => {
        state.supplierSummaryLoading = false;
        state.supplierSummaryError = action.payload as string || 'Failed to fetch supplier summary';
      });

    builder
      .addCase(fetchTransactionSummaryByTypeThunk.pending, (state) => {
        state.byTypeSummaryLoading = true;
        state.byTypeSummaryError = undefined;
      })
      .addCase(fetchTransactionSummaryByTypeThunk.fulfilled, (state, action) => {
        state.byTypeSummaryLoading = false;
        state.byTypeSummaryReport = action.payload;
        state.byTypeSummaryError = undefined;
      })
      .addCase(fetchTransactionSummaryByTypeThunk.rejected, (state, action) => {
        state.byTypeSummaryLoading = false;
        state.byTypeSummaryError = action.payload as string || 'Failed to fetch by-type transaction summary';
      });

    builder
      .addCase(fetchTransactionRatioPaymentRevenueThunk.pending, (state) => {
        state.ratioSummaryLoading = true;
        state.ratioSummaryError = undefined;
      })
      .addCase(fetchTransactionRatioPaymentRevenueThunk.fulfilled, (state, action) => {
        state.ratioSummaryLoading = false;
        state.ratioSummaryReport = action.payload;
        state.ratioSummaryError = undefined;
      })
      .addCase(fetchTransactionRatioPaymentRevenueThunk.rejected, (state, action) => {
        state.ratioSummaryLoading = false;
        state.ratioSummaryError = action.payload as string || 'Failed to fetch payment/revenue ratio summary';
      });

    // Create transaction
    builder
      .addCase(createTransactionThunk.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(createTransactionThunk.fulfilled, (state, action) => {
        state.loading = false;
        // Handle both direct payload and nested transaction object
        const responseData = action.payload;
        const createdTransaction = responseData.transaction || responseData;
        // Check if transaction already exists (to avoid duplicates)
        const existingIndex = state.transactions.findIndex(t => t.id === createdTransaction.id);
        if (existingIndex === -1) {
          state.transactions.unshift(createdTransaction);
        } else {
          // Update existing transaction
          state.transactions[existingIndex] = createdTransaction;
        }
      })
      .addCase(createTransactionThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to create transaction';
      });

    // Update transaction
    builder
      .addCase(updateTransactionThunk.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(updateTransactionThunk.fulfilled, (state, action) => {
        state.loading = false;
        const updatedTransaction = action.payload;
        const index = state.transactions.findIndex(t => t.id === updatedTransaction.id);
        if (index !== -1) {
          state.transactions[index] = updatedTransaction;
        }
        if (state.transaction?.id === updatedTransaction.id) {
          state.transaction = updatedTransaction;
        }
      })
      .addCase(updateTransactionThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to update transaction';
      });

    // Delete transaction
    builder
      .addCase(deleteTransactionThunk.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(deleteTransactionThunk.fulfilled, (state, action) => {
        state.loading = false;
        const deletedId = action.payload.id;
        state.transactions = state.transactions.filter(t => t.id !== deletedId);
        if (state.transaction?.id === deletedId) {
          state.transaction = undefined;
        }
      })
      .addCase(deleteTransactionThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to delete transaction';
      });

    // Bulk approve transactions
    builder
      .addCase(bulkApproveTransactionsThunk.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(bulkApproveTransactionsThunk.fulfilled, (state, action) => {
        state.loading = false;
        // Update the status of approved transactions in the state
        const approvedIds = action.payload.approved_transaction_ids || [];
        
        state.transactions = state.transactions.map(transaction => {
          if (approvedIds.includes(transaction.id)) {
            return {
              ...transaction,
              status: 'approved',
              approved_at: new Date().toISOString()
            };
          }
          return transaction;
        });
        
      })
      .addCase(bulkApproveTransactionsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to approve transactions';
      });
  },
});

// Export actions
export const {
  setFilter,
  setFilters,
  clearFilters,
  applyFilters,
  clearAppliedFilters,
  clearError,
  clearSummaryError,
  clearMonthlySummaryError,
  clearSupplierSummaryError,
  clearByTypeSummaryError,
  clearRatioSummaryError,
  updateTransaction,
} = transactionSlice.actions;

// Export reducer
export const reducer = transactionSlice.reducer;

export const actions = {
  requestTransactions: () => ({
    type: actionTypes.TransactionsRequested,
  }),
  fulfillTransactions: (transactions: Transaction[]) => ({
    type: actionTypes.TransactionsLoaded,
    payload: {transactions},
  }),
  failTransactions: (error: string) => ({
    type: actionTypes.TransactionsFailed,
    payload: {error},
  }),
  requestTransaction: () => ({
    type: actionTypes.TransactionRequested,
  }),
  fulfillTransaction: (transaction: Transaction) => ({
    type: actionTypes.TransactionLoaded,
    payload: {transaction},
  }),
  failTransaction: (error: string) => ({
    type: actionTypes.TransactionFailed,
    payload: {error},
  }),
  createTransaction: (transaction: Transaction) => ({
    type: actionTypes.TransactionCreated,
    payload: {transaction},
  }),
  updateTransaction: (transaction: Transaction) => ({
    type: actionTypes.TransactionUpdated,
    payload: {transaction},
  }),
  deleteTransaction: (transaction: Transaction) => ({
    type: actionTypes.TransactionDeleted,
    payload: {transaction},
  }),
}

// Thunk function to fetch transactions from API
export const fetchTransactionsThunk = (params?: { 
  dateFrom?: string; 
  dateTo?: string;
  documentDateFrom?: string;
  documentDateTo?: string;
  limit?: number;
  offset?: number;
}) => async (dispatch: any) => {
  try {
    dispatch(actions.requestTransactions());
    
    // Call real API endpoint
    const { default: axiosInstance } = await import('@/setup/axios/axiosInstance');
    
    // Build query params
    const queryParams: any = {
      limit: params?.limit || 1000,
      offset: params?.offset || 0,
    };
    
    // Add date filters if provided
    if (params?.dateFrom) {
      queryParams.date_from = params.dateFrom;
    }
    if (params?.dateTo) {
      queryParams.date_to = params.dateTo;
    }
    if (params?.documentDateFrom) {
      queryParams.document_date_from = params.documentDateFrom;
    }
    if (params?.documentDateTo) {
      queryParams.document_date_to = params.documentDateTo;
    }
    
    const response = await axiosInstance.get('/finance/transactions/', {
      params: queryParams
    });
    const transactions = response.data.results || response.data || [];
    
    dispatch(actions.fulfillTransactions(transactions));
  } catch (error: any) {
    dispatch(actions.failTransactions(error.response?.data?.message || 'Failed to fetch transactions'));
  }
};

// Bulk approve transactions thunk
export const bulkApproveTransactionsThunk = createAsyncThunk(
  'transactions/bulkApprove',
  async (transactionIds: string[], { rejectWithValue }) => {
    try {
      const { default: axiosInstance } = await import('@/setup/axios/axiosInstance');
      
      const response = await axiosInstance.post('/finance/transactions/bulk_approve/', {
        transaction_ids: transactionIds
      });
      
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to approve transactions');
    }
  }
);
